import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE_ID = 'ruvine-gift-sync';
// Enable after the checkout validator containing Check In gift IDs is released.
const CHECKIN_GIFTS_READY = true;
const CHECKIN_GIFT_KEYS = new Set(['shellBracelet', 'stripedCosmeticBag', 'stripedToteBag']);
const KEYCHARM_SOLD_OUT_MESSAGE = 'Random Keycharm is sold out. This free gift is no longer included.';
const GIFT_LABELS = {
  shellBracelet: 'RUVINE Shell Bracelet (Random)',
  stripedCosmeticBag: 'RUVINE Striped Cosmetic Bag',
  stripedToteBag: 'RUVINE Striped Tote Bag',
  scrunchie: 'Mrs. Show Hair Scrunchie',
  lashCurler: 'Lash Curler',
  hairScalp: 'Hair Scalp',
  dumplingPouch: 'Dumpling Pouch',
  pinkMiniPouch: 'Pink Mini Pouch',
  nukdRefill: 'NUKD Refill',
  keycharm: 'Random Keycharm',
  tattooSticker: 'Random Tattoo Sticker',
  hairClip: 'Random Hair Clip',
  hairband: 'Random Hairband',
  mysteryBag: 'Mystery Bag',
  tumbler: 'Tumbler Full Set',
  sunglasses: 'RUVINE Sunglasses',
  sunHat: 'RUVINE Sun Hat',
  miniUmbrella: 'RUVINE Mini Umbrella',
  accessoriesPouchM: 'RUVINE Accessories Pouch (M)',
  accessoriesPouchS: 'RUVINE Accessories Pouch (S)',
};
const GIFT_VARIANTS = {
  shellBracelet: [68327089045739, 68327189512427, 68327189545195],
  stripedCosmeticBag: 68327089144043,
  stripedToteBag: 68327089209579,
  scrunchie: 49332559675627,
  lashCurler: 49358053605611,
  hairScalp: 49304646910187,
  dumplingPouch: 49304647008491,
  pinkMiniPouch: 49332559610091,
  // Use the main NUKD product's Refill Pack variant so normal sales, PWP and
  // bundle gifts all deduct from the same Shopify inventory item.
  nukdRefill: 49246426988779,
  hairClip: [68168111128811, 68168159166699, 68168166375659, 68168173781227, 68168178532587, 68168188100843],
  mysteryBag: [68168118370539],
  hairband: [68168120828139, 68168130396395, 68168139866347],
  keycharm: [68168147042539, 68168154349803, 68168183283947],
  tattooSticker: [68168163950827],
  tumbler: 49358053834987,
  sunglasses: 67564745916651,
  sunHat: 67564895830251,
  miniUmbrella: 67564877480171,
  accessoriesPouchM: 67564907561195,
  accessoriesPouchS: 67564930531563,
};

const GIFT_VARIANT_IDS = new Set(Object.values(GIFT_VARIANTS).flat());
// Recognize old cart lines during migration so they are replaced, rather than
// duplicated, when the same physical item is now served by its PWP product.
const LEGACY_GIFT_VARIANT_IDS = new Set([
  49332559511787,
  49309583507691,
  49310698504427,
  49309586030827,
  49358044430571,
  49358062289131,
  49358062321899,
]);
const ALL_GIFT_VARIANT_IDS = new Set([...GIFT_VARIANT_IDS, ...LEGACY_GIFT_VARIANT_IDS]);

const RULES = new Map([
  // Halloween campaign: one random design per eligible gift category.
  [49072352133355, []],
  [49072349839595, []],
  [49072352166123, ["shellBracelet"]],
  [49072349872363, ["shellBracelet"]],
  [49072353247467, ["shellBracelet"]],
  [49072353181931, ["shellBracelet"]],
  [49072353214699, ["shellBracelet"]],
  [49367878926571, ["shellBracelet"]],
  [49072352198891, ["shellBracelet", "stripedCosmeticBag"]],
  [49072349905131, ["shellBracelet", "stripedCosmeticBag"]],
  [49072354722027, ["shellBracelet", "stripedCosmeticBag"]],
  [49072354754795, ["shellBracelet", "stripedCosmeticBag"]],
  [49072354689259, ["shellBracelet", "stripedCosmeticBag"]],
  [49072354656491, ["shellBracelet", "stripedCosmeticBag"]],
  [49072352231659, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072349937899, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355934443, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355901675, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355967211, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355868907, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355836139, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49072355803371, ["shellBracelet", "stripedCosmeticBag", "stripedToteBag"]],
  [49367951507691, ['lashCurler', 'hairScalp', 'dumplingPouch']],
  [49367922376939, ['lashCurler', 'pinkMiniPouch', 'nukdRefill']],

  // SunDay Kit: both set options include the four gifts shown in the campaign artwork.
  [67566774616299, ['sunglasses', 'miniUmbrella', 'sunHat', 'accessoriesPouchS']],
  [67566774649067, ['sunglasses', 'miniUmbrella', 'sunHat', 'accessoriesPouchS']],
]);

// Keep redeemed categories as display-only rows, never as order line items.
const REDEEMED_CAMPAIGN_VARIANTS = new Set(
  [...RULES].filter(([, gifts]) => gifts.includes('tattooSticker')).map(([id]) => id)
);
for (const variantId of REDEEMED_CAMPAIGN_VARIANTS) {
  RULES.set(variantId, RULES.get(variantId).filter((gift) => gift !== 'tattooSticker'));
}

function itemsForVariant(variantId, quantity = 1, { includeGifts = true, includeKeycharm = true } = {}) {
  const parentVariant = String(variantId);
  const gifts = RULES.get(Number(variantId)) || [];

  return gifts
    // Keycharm is added separately by the cart sync. This means an out-of-stock
    // Keycharm can never reject the customer's Bundle add-to-cart request.
    .filter((giftKey) => includeGifts && (CHECKIN_GIFTS_READY || !CHECKIN_GIFT_KEYS.has(giftKey)) && (includeKeycharm || giftKey !== 'keycharm'))
    .flatMap((giftKey) => allocateGiftVariants(giftKey, quantity).map(({ giftVariantId, quantity: giftQuantity }) => ({
      id: giftVariantId,
      quantity: giftQuantity,
      properties: {
        _ruvine_gift: 'true',
        _ruvine_gift_key: giftKey,
        _ruvine_parent_variant: parentVariant,
      },
    })));
}

function giftVariantIds(giftKey) {
  const variants = GIFT_VARIANTS[giftKey];
  return Array.isArray(variants) ? variants : [variants];
}

function chooseGiftVariant(giftKey) {
  const variants = giftVariantIds(giftKey);
  return variants[Math.floor(Math.random() * variants.length)];
}

function allocateGiftVariants(giftKey, quantity, existing = []) {
  if (giftKey !== 'shellBracelet') return [{ giftVariantId: chooseGiftVariant(giftKey), quantity }];
  const variants = giftVariantIds(giftKey);
  const allocations = new Map();
  let remaining = Math.max(0, Math.trunc(Number(quantity) || 0));
  for (const item of existing) {
    const variantId = Number(item.variant_id);
    if (!variants.includes(variantId)) continue;
    const keep = Math.min(remaining, Math.max(0, Math.trunc(Number(item.quantity) || 0)));
    if (keep) allocations.set(variantId, (allocations.get(variantId) || 0) + keep);
    remaining -= keep;
  }
  while (remaining > 0) {
    const variantId = chooseGiftVariant(giftKey);
    allocations.set(variantId, (allocations.get(variantId) || 0) + 1);
    remaining -= 1;
  }
  return [...allocations].map(([giftVariantId, quantity]) => ({ giftVariantId, quantity }));
}

window.RuvineGiftSync = { itemsForVariant };

let syncing = false;
let syncQueued = false;
let timer;
// A sold-out gift is retried on a new page load. During this visit, remember it
// so the sync does not repeatedly remove and re-add the other gifts.
const unavailableGiftKeys = new Set();

function getDesiredGifts(cart) {
  const desired = [];

  const parents = new Map();
  for (const item of cart.items) {
    if (item.properties?._ruvine_gift === 'true' || ALL_GIFT_VARIANT_IDS.has(Number(item.variant_id))) continue;
    const id = Number(item.variant_id);
    const existing = parents.get(id);
    parents.set(id, { ...item, quantity: (existing?.quantity || 0) + (Number(item.quantity) || 0) });
  }
  for (const item of parents.values()) {
    if (ALL_GIFT_VARIANT_IDS.has(Number(item.variant_id))) continue;
    const gifts = RULES.get(Number(item.variant_id));
    if (!gifts) continue;

    for (const gift of gifts) {
      if (!CHECKIN_GIFTS_READY && CHECKIN_GIFT_KEYS.has(gift)) continue;
      if (unavailableGiftKeys.has(gift)) continue;
      const parentVariant = String(item.variant_id);
      if (gift === 'shellBracelet') {
        const existing = cart.items.filter((line) => line.properties?._ruvine_gift === 'true'
          && line.properties?._ruvine_gift_key === gift
          && line.properties?._ruvine_parent_variant === parentVariant);
        desired.push(...allocateGiftVariants(gift, item.quantity, existing).map((allocation) => ({
          giftKey: gift, parentVariant, ...allocation,
        })));
        continue;
      }
      const existingGift = cart.items.find(
        (cartItem) =>
          cartItem.properties?._ruvine_gift === 'true' &&
          cartItem.properties?._ruvine_gift_key === gift &&
          cartItem.properties?._ruvine_parent_variant === parentVariant &&
          giftVariantIds(gift).includes(Number(cartItem.variant_id))
      );
      desired.push({
        giftKey: gift,
        giftVariantId: existingGift ? Number(existingGift.variant_id) : chooseGiftVariant(gift),
        parentVariant,
        // A bundle variant already represents its internal bottle count (for
        // example, “5 Bottles”), but the cart line can still contain multiple
        // copies of that bundle. Each configured gift must therefore match the
        // paid parent line quantity: two 5-bottle bundles receive two of every
        // included gift, not one.
        quantity: Math.max(1, Number(item.quantity) || 1),
      });
    }
  }

  return desired;
}

function giftsAreCorrect(cart, desired) {
  const giftLines = cart.items.filter(
    (item) =>
      ALL_GIFT_VARIANT_IDS.has(Number(item.variant_id)) &&
      item.properties?._ruvine_gift === 'true'
  );
  if (giftLines.length !== desired.length) return false;

  return desired.every((expected) => giftLines.some((item) => {
    const giftKey = item.properties?._ruvine_gift_key;
    return (
      item.properties?._ruvine_gift === 'true' &&
      expected.giftVariantId === Number(item.variant_id) &&
      giftKey === expected.giftKey &&
      item.properties?._ruvine_parent_variant === expected.parentVariant &&
      expected.quantity === item.quantity
    );
  }));
}

function matchesDesiredGift(item, expected) {
  return (
    item.properties?._ruvine_gift === 'true' &&
    expected.giftVariantId === Number(item.variant_id) &&
    item.properties?._ruvine_gift_key === expected.giftKey &&
    item.properties?._ruvine_parent_variant === expected.parentVariant &&
    expected.quantity === item.quantity
  );
}

function getGiftChanges(cart, desired) {
  const unmatchedGiftLines = cart.items.filter(
    (item) =>
      ALL_GIFT_VARIANT_IDS.has(Number(item.variant_id)) &&
      item.properties?._ruvine_gift === 'true'
  );
  const missing = [];

  for (const expected of desired) {
    const matchedIndex = unmatchedGiftLines.findIndex((item) => matchesDesiredGift(item, expected));
    if (matchedIndex === -1) {
      missing.push(expected);
    } else {
      unmatchedGiftLines.splice(matchedIndex, 1);
    }
  }

  return { missing, obsolete: unmatchedGiftLines };
}

function arrangeGiftRows() {
  document.querySelectorAll('cart-items-component').forEach((component) => {
    const mainRows = Array.from(component.querySelectorAll('.cart-items__table-row:not([data-ruvine-gift="true"])'));
    const giftRows = Array.from(component.querySelectorAll('.cart-items__table-row[data-ruvine-gift="true"]'));
    const setEndRows = new Set();

    for (const mainRow of mainRows) {
      const parentVariant = mainRow.dataset.variantId;
      const giftOrder = [...(RULES.get(Number(parentVariant)) || [])];
      if (REDEEMED_CAMPAIGN_VARIANTS.has(Number(parentVariant))) giftOrder.push('tattooSticker', 'keycharm', 'hairClip');
      const matchingGifts = giftRows
        .filter((row) => row.dataset.ruvineParentVariant === parentVariant)
        .sort((a, b) => giftOrder.indexOf(a.dataset.ruvineGiftKey) - giftOrder.indexOf(b.dataset.ruvineGiftKey));
      let anchor = mainRow;

      for (const giftRow of matchingGifts) {
        if (anchor.nextElementSibling !== giftRow) anchor.after(giftRow);
        anchor = giftRow;
      }

      if (anchor.nextElementSibling) setEndRows.add(anchor);
    }

    component.querySelectorAll('.cart-items__table-row').forEach((row) => {
      row.classList.toggle('ruvine-cart-set-end', setEndRows.has(row));
    });
  });
}

async function request(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.description || data?.message || `Gift sync failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

function isSoldOutError(error) {
  return error?.status === 422 && /sold out|out of stock|not enough inventory/i.test(error.message || '');
}

function setGiftAvailabilityMessages() {
  document.querySelectorAll('.ruvine-gift-availability-message').forEach((message) => message.remove());
}

async function syncGifts() {
  if (syncing) {
    syncQueued = true;
    return;
  }
  syncing = true;

  try {
    let cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    const desired = getDesiredGifts(cart);

    if (giftsAreCorrect(cart, desired)) {
      setGiftAvailabilityMessages();
      return;
    }

    const { missing, obsolete } = getGiftChanges(cart, desired);
    if (obsolete.length) {
      const updates = Object.fromEntries(obsolete.map((item) => [item.key, 0]));
      await request('/cart/update.js', { updates });
    }

    // Keep valid gifts in place and only add what is missing. The former
    // remove-everything-and-add-again approach could leave a three-gift bundle
    // temporarily incomplete when cart updates arrived close together.
    // Add missing gifts in one request first, then fall back per gift only if
    // Shopify reports that one of the variants is unavailable.
    let batchAdded = false;
    if (missing.length) {
      try {
        await request('/cart/add.js', {
          items: missing.map(({ giftKey, giftVariantId, parentVariant, quantity }) => ({
            id: giftVariantId,
            quantity,
            properties: {
              _ruvine_gift: 'true',
              _ruvine_gift_key: giftKey,
              _ruvine_parent_variant: parentVariant,
            },
          })),
        });
        missing.forEach(({ giftKey }) => unavailableGiftKeys.delete(giftKey));
        batchAdded = true;
      } catch (error) {
        console.warn('[Ruvine gifts] Batch add was unavailable; adding gifts individually.', error);
      }
    }

    // The slow fallback is only used for an unavailable gift. Shopify rejects
    // a multi-item request when any one variant is sold out.
    let keycharmSoldOut = false;
    for (const { giftKey, giftVariantId, parentVariant, quantity } of batchAdded ? [] : missing) {
      const item = {
        id: giftVariantId,
        quantity,
        properties: {
          _ruvine_gift: 'true',
          _ruvine_gift_key: giftKey,
          _ruvine_parent_variant: parentVariant,
        },
      };

      try {
        await request('/cart/add.js', { items: [item] });
      } catch (error) {
        let recovered = false;
        let lastError = error;
        if (isSoldOutError(error)) {
          for (const fallbackId of giftVariantIds(giftKey).filter((id) => id !== giftVariantId)) {
            try {
              await request('/cart/add.js', { items: [{ ...item, id: fallbackId }] });
              recovered = true;
              break;
            } catch (fallbackError) {
              lastError = fallbackError;
              if (!isSoldOutError(fallbackError)) break;
            }
          }
        }
        if (recovered) continue;
        if (isSoldOutError(lastError)) {
          unavailableGiftKeys.add(giftKey);
          if (giftKey === 'keycharm') keycharmSoldOut = true;
          console.warn(`[Ruvine gifts] ${giftKey} is unavailable and was skipped.`, lastError);
        } else {
          console.error(`[Ruvine gifts] ${giftKey} could not be added.`, lastError);
        }
        continue;
      }
    }

    if (!keycharmSoldOut) unavailableGiftKeys.delete('keycharm');
    setGiftAvailabilityMessages();

    cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        source: SOURCE_ID,
        itemCount: cart.item_count,
        suppressDrawerOpen: true,
      })
    );
    setTimeout(arrangeGiftRows, 250);
  } catch (error) {
    console.error('[Ruvine gifts]', error);
  } finally {
    syncing = false;
    if (syncQueued) {
      syncQueued = false;
      syncGifts();
    }
  }
}

function scheduleSync(event) {
  if (event?.detail?.sourceId === SOURCE_ID || event?.detail?.data?.source === SOURCE_ID) {
    setTimeout(arrangeGiftRows, 250);
    return;
  }
  // An inventory request can fail briefly while Shopify is updating the cart.
  // A later customer cart action must try that gift again instead of leaving it
  // permanently skipped for the rest of the browsing session.
  unavailableGiftKeys.clear();
  clearTimeout(timer);
  timer = setTimeout(syncGifts, 0);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleSync);

const cartObserver = new MutationObserver(() => requestAnimationFrame(arrangeGiftRows));
cartObserver.observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncGifts, { once: true });
} else {
  syncGifts();
}

requestAnimationFrame(arrangeGiftRows);
